import React, { useState } from 'react';
import { X, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface DataAggregationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAggregate: (startDate: string, endDate: string) => void;
    isLoading: boolean;
}

export const DataAggregationModal: React.FC<DataAggregationModalProps> = ({
    isOpen,
    onClose,
    onAggregate,
    isLoading
}) => {
    const [periodType, setPeriodType] = useState<'date' | 'month' | 'year'>('date');
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

    const handleSubmit = () => {
        let finalStartDate = '';
        let finalEndDate = '';

        if (periodType === 'date') {
            finalStartDate = startDate;
            finalEndDate = endDate;
        } else if (periodType === 'month') {
            // First and last day of month
            const [year, month] = selectedMonth.split('-');
            finalStartDate = `${selectedMonth}-01`;
            const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
            finalEndDate = `${selectedMonth}-${lastDay}`;
        } else if (periodType === 'year') {
            finalStartDate = `${selectedYear}-01-01`;
            finalEndDate = `${selectedYear}-12-31`;
        }

        onAggregate(finalStartDate, finalEndDate);
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div
                className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
            >
                <div className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden relative data-aggregation-modal-container">

                    {/* CRITICAL VISIBILITY OVERRIDES */}
                    <style>{`
                        /* Force Light Mode on this container */
                        .data-aggregation-modal-container {
                            color-scheme: light !important;
                        }

                        /* HEADERS & LABELS */
                        .data-aggregation-modal-container h3,
                        .data-aggregation-modal-container label {
                            color: #000000 !important;
                            font-weight: 800 !important;
                            opacity: 1 !important;
                            text-shadow: none !important;
                        }
                        
                        /* HEADER OVERRIDE: SPECIFICALLY TARGET THE HEADER BACKGROUND */
                        .data-aggregation-modal-container .modal-header {
                             background: linear-gradient(to right, #0084CF, #3EA3DA) !important;
                             background-color: #0084CF !important;
                        }
                        .data-aggregation-modal-container .modal-header * {
                            color: #ffffff !important;
                        }

                        /* INPUTS */
                        .data-aggregation-modal-container input,
                        .data-aggregation-modal-container select {
                            background-color: #ffffff !important; /* Pure White BG */
                            border: 2px solid #000000 !important; /* Black Border */
                            color: #000000 !important; /* Black Text */
                            font-weight: 900 !important; /* Extra Bold */
                            font-size: 16px !important;
                            opacity: 1 !important;
                            visibility: visible !important;
                            min-height: 45px !important;
                            padding: 8px 12px !important;
                            border-radius: 6px !important;
                            display: block !important;
                            width: 100% !important;
                            -webkit-text-fill-color: #000000 !important;
                            box-shadow: none !important;
                            /* Reset weird browser styles */
                            line-height: normal !important;
                            appearance: none !important;
                            -webkit-appearance: none !important;
                        }

                        /* NEW: Explicitly target input[type="date"] to override any specifics */
                        .data-aggregation-modal-container input[type="date"],
                        .data-aggregation-modal-container input[type="month"] {
                            align-items: center;
                            display: inline-flex;
                            font-family: inherit;
                        }

                        /* CRITICAL: Target the date value content directly */
                        .data-aggregation-modal-container input::-webkit-date-and-time-value {
                            color: #000000 !important;
                            -webkit-text-fill-color: #000000 !important;
                            min-height: 1.2em;
                        }

                        /* DATE INPUT SPECIFICS - TARGETING ALL SHADOW DOM PARTS */
                        .data-aggregation-modal-container input::-webkit-datetime-edit,
                        .data-aggregation-modal-container input::-webkit-datetime-edit-fields-wrapper,
                        .data-aggregation-modal-container input::-webkit-datetime-edit-text,
                        .data-aggregation-modal-container input::-webkit-datetime-edit-month-field,
                        .data-aggregation-modal-container input::-webkit-datetime-edit-day-field,
                        .data-aggregation-modal-container input::-webkit-datetime-edit-year-field {
                            color: #000000 !important;
                            -webkit-text-fill-color: #000000 !important;
                            opacity: 1 !important;
                        }
                        
                        /* CALENDAR ICON IN INPUT */
                        .data-aggregation-modal-container input::-webkit-calendar-picker-indicator {
                            filter: invert(0) !important; /* Ensure it's black */
                            opacity: 1 !important;
                            cursor: pointer;
                        }

                        /* BUTTONS - PERIOD SELECTOR TEXT SPECIFICS */
                        .data-aggregation-modal-container button.period-selector {
                            border: 1px solid #d1d5db !important;
                        }
                        /* FORCE EXTRA BOLD TEXT FOR BOTH ACTIVE AND INACTIVE */
                        .data-aggregation-modal-container button.period-selector span {
                            font-weight: 900 !important; /* MAXIMUM BOLDNESS */
                            font-family: inherit !important;
                        }

                        .data-aggregation-modal-container button.period-selector.active {
                            background-color: #0084CF !important; /* Blue BG */
                            border: 2px solid #0066A3 !important;
                        }
                        .data-aggregation-modal-container button.period-selector.active * {
                            color: #ffffff !important; /* White Text */
                            -webkit-text-fill-color: #ffffff !important;
                        }
                        .data-aggregation-modal-container button.period-selector.inactive {
                            background-color: #f3f4f6 !important; /* Light Gray BG */
                        }
                        .data-aggregation-modal-container button.period-selector.inactive * {
                            color: #000000 !important; /* Black Text */
                        }

                        /* ACTION BUTTONS FIX */
                         .data-aggregation-modal-container button.cancel-action {
                            background-color: #ffffff !important;
                            border: 2px solid #374151 !important; /* Visible Dark Gray Border - Thicker */
                            color: #1f2937 !important; /* Dark Gray Text */
                            -webkit-text-fill-color: #1f2937 !important;
                            font-weight: 700 !important;
                            display: flex !important;
                            align-items: center !important;
                            justify-content: center !important;
                            min-width: 100px !important;
                         }
                         .data-aggregation-modal-container button.cancel-action:hover {
                            background-color: #f3f4f6 !important;
                         }

                        .data-aggregation-modal-container button.primary-action {
                            background-color: #0084CF !important;
                            border: 2px solid #0084CF !important;
                            color: #ffffff !important;
                            -webkit-text-fill-color: #ffffff !important;
                            font-weight: 700 !important;
                            display: flex !important;
                            align-items: center !important;
                            justify-content: center !important;
                            min-width: 140px !important;
                        }
                        .data-aggregation-modal-container button.primary-action * {
                            color: #ffffff !important; /* Ensure children span/icons are white */
                        }
                        .data-aggregation-modal-container button.primary-action:hover {
                            background-color: #0066A3 !important; /* Darker blue on hover */
                            border-color: #0066A3 !important;
                        }

                    `}</style>

                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#0066A3] to-[#0084CF] text-white modal-header">
                        <h3 className="text-lg font-bold flex items-center gap-2">
                            <Calendar size={18} color="#ffffff" />
                            <span>Data Aggregation</span>
                        </h3>
                        <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full transition-colors">
                            <X size={18} color="#ffffff" />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="p-6 space-y-6 bg-white">
                        <div className="space-y-3">
                            <label className="block mb-1 text-black font-extrabold">Aggregation Period</label>
                            <div className="flex bg-gray-100 p-1.5 rounded-lg gap-2">
                                {(['date', 'month', 'year'] as const).map((type) => (
                                    <button
                                        key={type}
                                        onClick={() => setPeriodType(type)}
                                        className={`flex-1 py-2 rounded-md transition-all period-selector ${periodType === type ? 'active' : 'inactive'}`}
                                    >
                                        <span className="font-bold text-sm" style={{ fontWeight: 900 }}>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="pt-2">
                            {periodType === 'date' && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex flex-col">
                                        <label className="block mb-1 text-black font-extrabold">Start Date</label>
                                        <input
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="flex flex-col">
                                        <label className="block mb-1 text-black font-extrabold">End Date</label>
                                        <input
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}

                            {periodType === 'month' && (
                                <div className="flex flex-col">
                                    <label className="block mb-1 text-black font-extrabold">Select Month</label>
                                    <input
                                        type="month"
                                        value={selectedMonth}
                                        onChange={(e) => setSelectedMonth(e.target.value)}
                                    />
                                </div>
                            )}

                            {periodType === 'year' && (
                                <div className="flex flex-col">
                                    <label className="block mb-1 text-black font-extrabold">Select Year</label>
                                    <select
                                        value={selectedYear}
                                        onChange={(e) => setSelectedYear(e.target.value)}
                                    >
                                        {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-4 bg-gray-50 border-t flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-bold rounded-md cancel-action"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={isLoading}
                            className="px-4 py-2 text-sm font-bold bg-[#0066A3] rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 primary-action"
                        >
                            {isLoading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    <span style={{ color: '#ffffff !important' }}>Processing...</span>
                                </>
                            ) : (
                                <span style={{ color: '#ffffff !important' }}>Aggregate Data</span>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </AnimatePresence>
    );
};
